import { useEffect, useRef } from 'react';

const animatedContentFrom = {
  opacity: 0.92,
  transform: 'translateX(6px)',
};

const animatedContentTo = {
  opacity: 1,
  transform: 'translateX(0)',
};

const animatedContentTransition: KeyframeAnimationOptions = {
  duration: 200,
  easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
};

/**
 * Adapts React Bits AnimatedContent's `fromTo` opacity path to shell view
 * changes. The source component uses GSAP/ScrollTrigger; this shell stays
 * mounted to preserve draft, focus, and scroll state, so its route-change
 * timeline maps to WAAPI keyframes instead. This keeps the source's
 * `initialOpacity` → `1` and `animateOpacity` `fromTo` path, maps
 * `power3.out` to a similar CSS ease-out, and retains the reduced-motion
 * bypass and interrupted-animation cleanup. A 220 ms view-key change replaces
 * `trigger="mount"`. Distance, scale, blur, rotation, wipe, stagger, and
 * ScrollTrigger effects stay unused to protect the dense quote table and its
 * sticky/fixed controls.
 *
 * Source: https://github.com/DavidHDev/react-bits
 * Component: src/ts-default/Animations/AnimatedContent/AnimatedContent.tsx
 * Upstream commit: 4dc7b5b7772e1217c8fb8d2a7aa074302180e5b4
 */
export function useShellViewMotion(view: string | null) {
  const contentRef = useRef<HTMLElement>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof content.animate !== 'function') return;

    const animation = content.animate(
      [animatedContentFrom, animatedContentTo],
      animatedContentTransition,
    );

    return () => animation.cancel();
  }, [view]);

  return contentRef;
}

export function useShellPickerMotion(
  contentRef: { current: HTMLElement | null },
  view: string | null,
  picker: string | null,
) {
  const previousViewRef = useRef(view);
  const previousPickerRef = useRef(picker);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      previousViewRef.current = view;
      previousPickerRef.current = picker;
      return;
    }

    const viewChanged = previousViewRef.current !== view;
    const pickerChanged = previousPickerRef.current !== picker;
    previousViewRef.current = view;
    previousPickerRef.current = picker;
    if (viewChanged || !pickerChanged || view !== 'workspace') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const panel = contentRef.current?.querySelector<HTMLElement>(
      '[aria-label="报价选品侧面板"]',
    );
    if (!panel || typeof panel.animate !== 'function') return;

    const animation = panel.animate(
      [
        { opacity: 0.9, transform: 'translateX(-6px)' },
        { opacity: 1, transform: 'translateX(0)' },
      ],
      {
        duration: 180,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      },
    );

    return () => animation.cancel();
  }, [contentRef, picker, view]);
}
